import React, { useEffect, useState } from 'react'

import Modal from '../../../components/common/Modal'
import SmallButton from './SmallButton'
import { Flex } from '../../../components/FlexBox'
import { H4, Text } from '../../../components/common/Text'
import { Switch } from '../../../components/common/Switch'
import Dropdown from '../../../components/common/Dropdown'
import TextInput from '../../../ui-library/TextInput'
import useContentTopicQuery from '../../../components/common/form/hooks/useContentTopicQuery'
import useTranslation from '../../../hooks/useTranslation'

export interface INewFeedInput {
  name: string
  description: string
  topicIds: string[]
  enabled: boolean
}

interface NewContentFeedModalProps {
  open: boolean
  close: () => void
  onCreate: (input: INewFeedInput) => Promise<void>
}

/**
 * Topics and visibility are asked for here rather than left to the settings
 * modal afterwards: a feed with no topics shows members nothing but its curated
 * items, so both are part of deciding what the feed IS, not incidental config.
 */
export const NewContentFeedModal = ({
  open,
  close,
  onCreate
}: NewContentFeedModalProps) => {
  const { t } = useTranslation()
  const { data } = useContentTopicQuery()
  const topicOptions = data?.contentTopics ?? []

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [topicIds, setTopicIds] = useState<string[]>([])
  const [enabled, setEnabled] = useState(false)
  const [saving, setSaving] = useState(false)

  // Reopening should not show the last attempt's values.
  useEffect(() => {
    if (!open) {
      setName('')
      setDescription('')
      setTopicIds([])
      setEnabled(false)
      setSaving(false)
    }
  }, [open])

  /**
   * Same rule the settings modal enforces: a feed with no topics shows members
   * nothing but its curated items. Unconditional here -- the featured feed
   * already exists, so nothing created through this modal is exempt.
   */
  const needsTopic = enabled && !topicIds.length

  const submit = async () => {
    if (!name.trim() || saving || needsTopic) return
    setSaving(true)
    try {
      await onCreate({ name, description, topicIds, enabled })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} close={close} showCloseIcon>
      <Flex p={2} style={{ width: 420, maxWidth: '100%' }}>
        <H4>{t('contentCuration:create:title')}</H4>
        <Text styles={{ fontSize: 12, marginBottom: 16 }}>
          {t('contentCuration:create:description')}
        </Text>

        <Text styles={{ fontSize: 14, marginBottom: 4 }}>
          {t('contentCuration:list:name')}
        </Text>
        <TextInput
          value={name}
          onChangeText={setName}
          placeholder={t('contentCuration:create:namePlaceholder')}
          autoFocus
          style={{ width: '100%' }}
        />

        <Text styles={{ fontSize: 14, marginBottom: 4 }}>
          {t('contentCuration:create:descriptionLabel')}
        </Text>
        <TextInput
          value={description}
          onChangeText={setDescription}
          placeholder={t('contentCuration:create:descriptionPlaceholder')}
          style={{ width: '100%' }}
        />

        <Text styles={{ fontSize: 14, marginBottom: 4 }}>
          {t('contentCuration:list:topics')}
        </Text>
        <Text styles={{ fontSize: 12, marginBottom: 4 }}>
          {t('contentCuration:settings:topicsNote')}
        </Text>
        <Dropdown
          options={topicOptions}
          value={topicIds}
          onSelect={next => setTopicIds(next as string[])}
          placeholder={t('contentCuration:settings:topicsPlaceholder')}
          isMulti
          shortMultiSelect
          closeMenuOnSelect={false}
          style={{ width: '100%' }}
        />

        <Flex flexDirection="row" alignItems="center" mb={3}>
          <Switch value={enabled} onValueChange={setEnabled} />
          <Text styles={{ fontSize: 14, marginLeft: 8 }}>
            {t('contentCuration:settings:visibilityLabel')}
          </Text>
        </Flex>

        {needsTopic && (
          <Text
            styles={{
              fontSize: 12,
              color: '#ED435C',
              marginTop: -8,
              marginBottom: 16
            }}
          >
            {t('contentCuration:settings:topicRequired')}
          </Text>
        )}

        <Flex flexDirection="row" justifyContent="flex-end">
          <Flex mr={2}>
            <SmallButton
              label={t('contentCuration:create:cancel')}
              onPress={close}
            />
          </Flex>
          <SmallButton
            label={t('contentCuration:create:submit')}
            onPress={submit}
            disabled={!name.trim() || saving || needsTopic}
            variant="primary"
          />
        </Flex>
      </Flex>
    </Modal>
  )
}

export default NewContentFeedModal
